import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { PrimeReactProvider } from "primereact/api";
import OverlayEscapeGuard from './components/ui/OverlayEscapeGuard';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { ProgressSpinner } from "@allygpo/ally-ux";
import { PRACTICES_SESSION_KEY, CATEGORIES_SESSION_KEY, PRODUCT_FAMILIES_SESSION_KEY, EXCLUDED_OPPORTUNITIES_SESSION_KEY, usePracticeData } from "./hooks/usePracticeData";

import Layout from "./components/Layout/Layout";
import Forbidden from "./pages/Forbidden/Forbidden";
import Dashboard from "./pages/Dashboard/Dashboard";
import ScenarioGenerator from "./pages/ScenarioGenerator/ScenarioGenerator";
import ProductComparison from "./pages/ProductComparison/ProductComparison";
import ScenarioResults from "./pages/ScenarioResults/ScenarioResults";
import FavoriteScenarios from "./pages/FavoriteScenarios/FavoriteScenarios";
import AccountSettings from "./pages/AccountSettings/AccountSettings";
import ComingSoon from "./pages/ComingSoon/ComingSoon";

import { ScenarioProvider, discardPersistedState, scenarioActions, useScenarioContext } from "./context/ScenarioContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useForbidden, setForbidden } from "./services/permissionState";

// Development-only gallery of the shared redesign components; never routed in production builds.
const UiKit = process.env.NODE_ENV === "development" ? React.lazy(() => import("./pages/UiKit/UiKit")) : null;

// Full-screen centered spinner used while we verify access.
const FullScreenSpinner: React.FC<{ label?: string }> = ({ label }) => (
  <div className="flex justify-content-center align-items-center" style={{ height: '100vh' }}>
    <div className="text-center">
      <ProgressSpinner
        style={{ width: '80px', height: '80px' }}
        strokeWidth="4"
      />
      {label && <h2 className="u-mt-5 text-xl font-bold text-tertiary">{label}</h2>}
    </div>
  </div>
);

// Gates the entire authenticated app behind the practices/role check.
// This component only mounts inside <AuthenticatedTemplate>, so the practices
// call only runs once the user is signed in.
//   - while the (uncached) practices call is in flight  -> spinner
//   - if it returns 403 (no required role)              -> Forbidden page
//   - otherwise                                         -> the app
const AuthenticatedApp: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
  const isForbidden = useForbidden();
  const { isLoading: practicesLoading } = usePracticeData();

  if (isForbidden) {
    return <Forbidden onLogout={onLogout} />;
  }

  if (practicesLoading) {
    return <FullScreenSpinner label="Loading..." />;
  }

  return (
    <Layout onLogout={onLogout}>
      <Routes>
        {/* Users land on Configure; Home has no nav entry but stays reachable at /dashboard. */}
        <Route path="/" element={<Navigate to="/account-settings" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/generate" element={<ScenarioGenerator />} />
        <Route path="/compare" element={<ProductComparison />} />
        <Route path="/results" element={<ScenarioResults />} />
        <Route path="/favorites" element={<FavoriteScenarios />} />
        <Route path="/account-settings" element={<AccountSettings />} />
        <Route path="/builder/results" element={<ComingSoon title="Builder results" />} />
        <Route path="/builder/favorites" element={<ComingSoon title="Builder favorites" />} />
        {UiKit && <Route path="/ui-kit" element={<React.Suspense fallback={null}><UiKit /></React.Suspense>} />}
        <Route path="*" element={<Navigate to="/account-settings" replace />} />
      </Routes>
    </Layout>
  );
};

const AppRoutes: React.FC = () => {
  const { dispatch } = useScenarioContext();
  const { logout } = useAuth();

  const handleLogout = () => {
    // First, so the clearAll below can't be written back to storage before the redirect leaves.
    discardPersistedState();
    setForbidden(false);
    dispatch(scenarioActions.clearAll());
    sessionStorage.removeItem(PRACTICES_SESSION_KEY);
    sessionStorage.removeItem(CATEGORIES_SESSION_KEY);
    sessionStorage.removeItem(PRODUCT_FAMILIES_SESSION_KEY);
    sessionStorage.removeItem(EXCLUDED_OPPORTUNITIES_SESSION_KEY);
    logout();
  };

  return (
    <>
      <AuthenticatedTemplate>
        <AuthenticatedApp onLogout={handleLogout} />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <FullScreenSpinner label="Redirecting..." />
      </UnauthenticatedTemplate>
    </>
  );
};

const App: React.FC = () => (
  <PrimeReactProvider>
    <OverlayEscapeGuard />
    <ScenarioProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ScenarioProvider>
  </PrimeReactProvider>
);

export default App;
